import {
	AlertCircle,
	ArrowDownUp,
	Ban,
	Check,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	Eye,
	EyeOff,
	FolderGit2,
	GitBranch,
	GitMerge,
	GitPullRequest,
	GitPullRequestClosed,
	Link2,
	Loader2,
	Plus,
	RefreshCcw,
	RotateCcw,
	Search,
	Tag,
	Trash2,
	User,
	X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ITEMS_PER_PAGE } from "../constants";
import {
	escapeRegExp,
	getMRAuthorName,
	getMRAuthorUsername,
	getMRProjectPath,
	isMRExcludedByRules,
} from "../exclusionRules";
import { fuzzyMatchFields, normalizeSearchQuery } from "../fuzzySearch";
import { useLocalStorage } from "../hooks/useLocalStorage";
import {
	isRepositoryCacheFresh,
	useRepositoryMergeRequests,
} from "../hooks/useRepositoryMergeRequests";
import { useSectionOrder } from "../hooks/useSectionOrder";
import { getWatchKey } from "../hooks/useWatchedMRs";
import { applyQuickFilters } from "../quickFilters";
import {
	DEFAULT_REPO_PATHS,
	buildCheckoutCommand,
	copyCheckoutCommand,
	copyMRLinks,
	safeHttpUrl,
} from "../utils";
import { CountChip } from "./CountChip";
import { HighlightedText } from "./HighlightedText";
import { MRTagDialog, TagPill, mrMatchesActiveTagFilters } from "./MRTags";
import { MergeRequest } from "./MergeRequest";
import { PaginationButtons } from "./PaginationButtons";
import { SECTION_DRAG_TYPE, SectionDragHandle } from "./SectionDragHandle";
import { Select } from "./Select";
import { Tooltip } from "./Tooltip";

const DEFAULT_REPO_STATES = ["opened", "merged", "closed"];
const STATE_FILTERS = [
	{ value: "opened", label: "Open", icon: GitPullRequest },
	{ value: "merged", label: "Merged", icon: GitMerge },
	{ value: "closed", label: "Closed", icon: GitPullRequestClosed },
];

const SORT_OPTIONS = [
	{ value: "newest", label: "Newest first" },
	{ value: "oldest", label: "Oldest first" },
];

const DEFAULT_SECTION_CONTROLS = { author: "", order: "newest", query: "" };

const getMRKey = getWatchKey;

const getProjectTagPath = (tag) =>
	typeof tag === "string" ? tag : tag?.id || tag?.text || "";

const buildExcludeDraft = (mr) => {
	const projectPath = getMRProjectPath(mr);
	const authorName = getMRAuthorName(mr);
	const authorUsername = getMRAuthorUsername(mr);

	return {
		mr,
		includeTitle: false,
		includeRepo: !!projectPath,
		includeAuthor: false,
		titleRegex: escapeRegExp(mr.title || ""),
		projectPath,
		authorName,
		authorUsername,
	};
};

const buildExcludeRuleFromMR = (mr, options) => ({
	titleRegex: options.includeTitle ? escapeRegExp(mr.title || "") : "",
	projectPath: options.includeRepo ? getMRProjectPath(mr) : "",
	authorName: options.includeAuthor ? getMRAuthorName(mr) : "",
	authorUsername: options.includeAuthor ? getMRAuthorUsername(mr) : "",
	sourceMr: {
		id: mr.id,
		iid: mr.iid,
		title: mr.title,
		projectPath: getMRProjectPath(mr),
	},
});

const clampContextMenuPosition = (x, y) => ({
	x: Math.max(8, Math.min(x, window.innerWidth - 216)),
	y: Math.max(8, Math.min(y, window.innerHeight - 64)),
});

const authorKey = (mr) => mr.author?.username || mr.author?.name || "";

const buildAuthorOptions = (mrs) => {
	const byKey = new Map();
	for (const mr of mrs) {
		const key = authorKey(mr);
		if (!key || byKey.has(key)) continue;
		byKey.set(key, mr.author?.name || key);
	}
	return [
		{ value: "", label: "All authors" },
		...[...byKey.entries()]
			.sort((a, b) => a[1].localeCompare(b[1]))
			.map(([value, label]) => ({ value, label })),
	];
};

const repoLabel = (repo) =>
	repo?.nameWithNamespace || repo?.fullPath || repo?.name || "Repository";

const repoName = (repo) =>
	repo?.name ||
	repo?.fullPath?.split("/").filter(Boolean).pop() ||
	repoLabel(repo);

const filterAndSortMRs = ({
	mrs,
	controls,
	excludeProjects = [],
	excludeLikeRules = [],
	ignoredMRIds = [],
	quickFilters,
	repoStates,
	searchQuery,
	showBotPRs,
	showDrafts,
	sortOption,
	activeTagFilter = [],
	getMRTags = () => [],
}) => {
	const selectedStates = new Set(repoStates);
	const excludedProjectPaths = new Set(
		excludeProjects.map(getProjectTagPath).filter(Boolean),
	);
	const globalQuery = normalizeSearchQuery(searchQuery);
	const repoQuery = normalizeSearchQuery(controls.query);
	const dateField = sortOption === "createdAt" ? "createdAt" : "updatedAt";
	const order = controls.order === "oldest" ? "oldest" : "newest";
	const matchesQuery = (mr, query) => {
		if (!query) return true;

		const fields = [
			mr.title,
			mr.author?.name,
			mr.author?.username,
			mr.projectPath,
			mr.iid && `!${mr.iid}`,
			mr.iid && `#${mr.iid}`,
			mr.sourceBranch,
		];
		return fuzzyMatchFields(fields, query);
	};

	return applyQuickFilters(
		mrs.filter((mr) => {
			if (!selectedStates.has(mr.state)) return false;
			if (!showDrafts && mr.draft) return false;
			if (!showBotPRs && (mr.author?.bot ?? false)) return false;
			if (excludedProjectPaths.has(getMRProjectPath(mr))) return false;
			if (ignoredMRIds.includes(mr.id)) return false;
			if (isMRExcludedByRules(mr, excludeLikeRules)) return false;
			if (!mrMatchesActiveTagFilters(getMRTags(mr), activeTagFilter)) {
				return false;
			}
			if (controls.author && authorKey(mr) !== controls.author) return false;
			return matchesQuery(mr, globalQuery) && matchesQuery(mr, repoQuery);
		}),
		quickFilters,
	).sort((a, b) => {
		const da = new Date(a[dateField] || a.updatedAt || a.createdAt || 0);
		const db = new Date(b[dateField] || b.updatedAt || b.createdAt || 0);
		return order === "oldest" ? da - db : db - da;
	});
};

const RepoSearchDropdown = ({ existingRepos, onAdd, searchProjects }) => {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const ref = useRef(null);
	const existingPaths = useMemo(
		() => new Set(existingRepos.map((repo) => repo.fullPath)),
		[existingRepos],
	);

	useEffect(() => {
		const handlePointerDown = (event) => {
			if (ref.current && !ref.current.contains(event.target)) setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	useEffect(() => {
		const term = query.trim();
		if (term.length < 2) {
			setResults([]);
			setError("");
			setLoading(false);
			return undefined;
		}

		let cancelled = false;
		setLoading(true);
		setError("");
		const timer = setTimeout(async () => {
			try {
				const projects = await searchProjects(term);
				if (!cancelled) {
					setResults(projects);
					setOpen(true);
				}
			} catch (searchError) {
				if (!cancelled) {
					setError(searchError.message);
					setResults([]);
					setOpen(true);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}, 250);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query, searchProjects]);

	const addRepo = (repo) => {
		onAdd(repo);
		setQuery("");
		setResults([]);
		setOpen(false);
	};

	return (
		<div
			ref={ref}
			className={`relative min-w-0 flex-1 ${open ? "z-[9999]" : "z-0"}`}
		>
			<div className="relative">
				<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
				<input
					type="text"
					value={query}
					onChange={(event) => {
						setQuery(event.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					placeholder="Search repositories"
					aria-label="Search repositories"
					className="h-8 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] pl-9 pr-9 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-border-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
				/>
				{loading ? (
					<Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--color-text-secondary)]" />
				) : query ? (
					<button
						type="button"
						onClick={() => {
							setQuery("");
							setResults([]);
							setError("");
						}}
						className="search-clear-button no-hover-lift absolute inset-y-0 right-2 my-auto inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Clear repository search"
					>
						<X className="h-4 w-4" />
					</button>
				) : null}
			</div>

			{open && query.trim().length >= 2 && (
				<div className="absolute left-0 right-0 top-full z-[9999] mt-1.5 max-h-80 w-full overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-1 shadow-xl">
					{error ? (
						<div className="flex items-center gap-2 px-3 py-2 text-xs text-[var(--color-error-text)]">
							<AlertCircle className="h-4 w-4 shrink-0" />
							<span className="truncate">{error}</span>
						</div>
					) : results.length === 0 && !loading ? (
						<div className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">
							No repositories found
						</div>
					) : (
						results.map((repo) => {
							const added = existingPaths.has(repo.fullPath);
							return (
								<button
									key={repo.fullPath}
									type="button"
									disabled={added}
									onClick={() => addRepo(repo)}
									className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
								>
									<FolderGit2 className="mt-0.5 h-4 w-4 shrink-0 self-start text-[var(--color-text-secondary)]" />
									<span className="min-w-0 flex-1">
										<span className="block truncate">{repoName(repo)}</span>
										<span className="block truncate text-xs text-[var(--color-text-secondary)]">
											{repo.fullPath}
										</span>
									</span>
									{added ? (
										<Check className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />
									) : (
										<Plus className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
									)}
								</button>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

const StateFilterBar = ({ states, onToggle }) => (
	<div className="flex flex-wrap items-center gap-2">
		{STATE_FILTERS.map(({ value, label, icon: Icon }) => {
			const active = states.includes(value);
			return (
				<button
					key={value}
					type="button"
					aria-pressed={active}
					onClick={() => onToggle(value)}
					className={`no-hover-lift inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium transition-colors ${
						active
							? "border-[var(--color-text-primary)]/70 bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
							: "border-[var(--color-border)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
					}`}
				>
					<Icon className="h-3.5 w-3.5" />
					{label}
				</button>
			);
		})}
	</div>
);

const SectionFilterControls = ({ authorOptions, value, onChange }) => (
	<div className="flex flex-wrap items-center gap-2">
		<MRSearchInput
			value={value.query}
			onChange={(query) => onChange({ query })}
		/>
		<SearchableSelect
			icon={User}
			ariaLabel="Filter by author"
			placeholder="All authors"
			value={value.author}
			options={authorOptions}
			onChange={(author) => onChange({ author })}
		/>
		<Select
			icon={ArrowDownUp}
			ariaLabel="Sort repository merge requests"
			value={value.order}
			options={SORT_OPTIONS}
			onChange={(order) => onChange({ order })}
		/>
	</div>
);

const MRSearchInput = ({ value, onChange }) => (
	<div className="relative min-w-48">
		<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
		<input
			type="text"
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder="Search MRs"
			aria-label="Search merge requests in this repository"
			className="h-8 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] pl-8 pr-8 text-xs font-medium text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-border-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
		/>
		{value && (
			<button
				type="button"
				onClick={() => onChange("")}
				className="search-clear-button no-hover-lift absolute inset-y-0 right-1.5 my-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
				aria-label="Clear repository merge request search"
			>
				<X className="h-3.5 w-3.5" />
			</button>
		)}
	</div>
);

const SearchableSelect = ({
	value,
	onChange,
	options,
	placeholder = "All",
	icon: Icon,
	ariaLabel,
}) => {
	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const ref = useRef(null);
	const inputRef = useRef(null);
	const optionRefs = useRef([]);
	const selected = options.find((option) => option.value === value);
	const label = selected ? selected.label : placeholder;
	const canClear =
		Boolean(value) && options.some((option) => option.value === "");
	const clearLabel = ariaLabel
		? `Clear ${ariaLabel.charAt(0).toLowerCase()}${ariaLabel.slice(1)}`
		: "Clear selection";
	const filteredOptions = useMemo(() => {
		const query = filter.trim().toLowerCase();
		if (!query) return options;

		return options.filter(
			(option) =>
				!option.value ||
				option.label.toLowerCase().includes(query) ||
				String(option.value).toLowerCase().includes(query),
		);
	}, [filter, options]);
	const selectOption = (option) => {
		onChange(option.value === value ? "" : option.value);
		setOpen(false);
	};

	useEffect(() => {
		if (!open) return undefined;

		setFilter("");
		setActiveIndex(0);
		const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
		const handlePointerDown = (event) => {
			if (ref.current && !ref.current.contains(event.target)) setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			clearTimeout(focusTimer);
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
	}, [activeIndex, open]);

	const handleFilterKeyDown = (event) => {
		if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;

		if (filteredOptions.length === 0) return;

		event.preventDefault();
		if (event.key === "ArrowDown") {
			setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
			return;
		}
		if (event.key === "ArrowUp") {
			setActiveIndex(
				(prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length,
			);
			return;
		}

		selectOption(filteredOptions[activeIndex]);
	};
	const clearSelection = (event) => {
		event.stopPropagation();
		onChange("");
		setFilter("");
		setOpen(false);
	};

	return (
		<div
			ref={ref}
			className={`relative inline-flex ${open ? "z-[9999]" : "z-0"}`}
		>
			<div
				className={`inline-flex h-8 min-w-0 items-center overflow-hidden rounded-lg border text-xs font-medium transition-colors focus-within:border-[var(--color-border-hover)] ${
					value
						? "border-[var(--color-border-hover)] bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
						: "border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-primary)]"
				}`}
			>
				<button
					type="button"
					aria-haspopup="listbox"
					aria-expanded={open}
					aria-label={ariaLabel}
					onClick={() => setOpen((prev) => !prev)}
					className="no-hover-lift inline-flex h-full min-w-0 flex-1 items-center gap-1.5 px-2.5 text-left transition-colors focus:outline-none"
				>
					{Icon && <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />}
					<span className="max-w-[10rem] truncate">{label}</span>
					<ChevronDown
						className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200 ${
							open ? "rotate-180" : ""
						}`}
					/>
				</button>
				{canClear && (
					<button
						type="button"
						onClick={clearSelection}
						className="no-hover-lift inline-flex h-full w-7 shrink-0 items-center justify-center border-l border-[var(--color-border)] text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none"
						aria-label={clearLabel}
						title={clearLabel}
					>
						<X className="h-3.5 w-3.5" />
					</button>
				)}
			</div>

			{open && (
				<div className="absolute left-0 top-full z-[9999] mt-1.5 max-h-80 min-w-[16rem] overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-1 shadow-xl">
					<div className="relative mb-1">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
						<input
							ref={inputRef}
							type="text"
							value={filter}
							onChange={(event) => {
								setFilter(event.target.value);
								setActiveIndex(0);
								optionRefs.current = [];
							}}
							onKeyDown={handleFilterKeyDown}
							placeholder="Filter authors"
							aria-label="Filter authors"
							className="h-8 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] pl-8 pr-2 text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						/>
					</div>

					{filteredOptions.length > 0 ? (
						filteredOptions.map((option, index) => {
							const active = option.value === value;
							const highlighted = index === activeIndex;
							return (
								<button
									key={option.value || "__all"}
									ref={(node) => {
										optionRefs.current[index] = node;
									}}
									type="button"
									aria-pressed={active}
									onMouseEnter={() => setActiveIndex(index)}
									onClick={() => selectOption(option)}
									className={`no-hover-lift flex h-8 w-full items-center justify-between gap-2 rounded-md px-2.5 text-left text-xs transition-colors ${
										active
											? "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
											: highlighted
												? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
												: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
									}`}
								>
									<span className="truncate">{option.label}</span>
									{active && (
										<Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-accent)]" />
									)}
								</button>
							);
						})
					) : (
						<div className="px-2.5 py-1.5 text-xs text-[var(--color-text-secondary)]">
							No authors found
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const MRContextMenu = ({
	menu,
	menuRef,
	ignoredMRIds,
	watchedIds,
	onCopyCheckout,
	onCopyMRLinks,
	onIgnoreMRs,
	onOpenRuleForm,
	onUnignoreMRs,
	onUnwatchMRs,
	onWatchMRs,
	allMRTags = [],
	getMRTags = () => [],
	onAddTagToMRs,
	onOpenTagDialog,
}) => {
	const [repoPaths] = useLocalStorage("repoPaths", DEFAULT_REPO_PATHS);
	if (!menu) return null;

	const targetMRs = menu.targetMRs?.length ? menu.targetMRs : [menu.mr];
	const targetMRsWithIds = targetMRs.filter((mr) => mr?.id);
	const ignoredTargets = targetMRsWithIds.filter((mr) =>
		ignoredMRIds.includes(mr.id),
	);
	const unignoredTargets = targetMRsWithIds.filter(
		(mr) => !ignoredMRIds.includes(mr.id),
	);
	const shouldUnignore =
		targetMRsWithIds.length > 0 &&
		ignoredTargets.length === targetMRsWithIds.length;
	const ignoreActionMRs = shouldUnignore ? ignoredTargets : unignoredTargets;
	const watchableTargets = targetMRs.filter(Boolean);
	const watchedTargets = watchableTargets.filter((mr) =>
		watchedIds.includes(getMRKey(mr)),
	);
	const shouldUnwatch =
		watchableTargets.length > 0 &&
		watchedTargets.length === watchableTargets.length;
	const watchActionMRs = shouldUnwatch
		? watchedTargets
		: watchableTargets.filter((mr) => !watchedIds.includes(getMRKey(mr)));
	const isBulk = targetMRs.length > 1;
	const mrNoun = isBulk ? "MRs" : "MR";
	const copyLinksLabel = `Copy link${isBulk ? `s (${targetMRs.length})` : ""}`;
	const watchVerb = shouldUnwatch ? "Unwatch" : "Watch";
	const watchLabel = `${watchVerb} ${mrNoun}${
		isBulk ? ` (${watchActionMRs.length})` : ""
	}`;
	const ignoreVerb = shouldUnignore ? "Unignore" : "Ignore";
	const ignoreLabel = `${ignoreVerb} ${mrNoun}${
		isBulk ? ` (${ignoreActionMRs.length})` : ""
	}`;
	const excludeLabel = `Exclude Like ${mrNoun}${
		isBulk ? ` (${targetMRs.length})` : ""
	}`;
	const quickTagOptions = allMRTags
		.filter((tag) =>
			targetMRs.some(
				(mr) =>
					!getMRTags(mr).some(
						(mrTag) => mrTag.toLowerCase() === tag.toLowerCase(),
					),
			),
		)
		.slice(0, 5);
	const checkoutCommand = buildCheckoutCommand(menu.mr, repoPaths);

	return (
		<div
			ref={menuRef}
			role="menu"
			className="fixed z-50 min-w-48 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-xl"
			style={{ left: menu.x, top: menu.y }}
		>
			{checkoutCommand && (
				<button
					type="button"
					role="menuitem"
					onClick={() => onCopyCheckout?.(menu.mr)}
					title={checkoutCommand}
					className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
				>
					<GitBranch className="h-4 w-4 text-[var(--color-text-secondary)]" />
					Copy checkout command
				</button>
			)}
			<button
				type="button"
				role="menuitem"
				onClick={() => onCopyMRLinks?.(targetMRs)}
				className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Link2 className="h-4 w-4 text-[var(--color-text-secondary)]" />
				{copyLinksLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={() => onOpenTagDialog?.(targetMRs)}
				className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Tag className="h-4 w-4 text-[var(--color-text-secondary)]" />
				Add tag{isBulk ? ` to ${targetMRs.length} MRs` : ""}
			</button>
			{quickTagOptions.length > 0 && (
				<div className="border-y border-[var(--color-border)] px-2 py-1">
					<div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
						Quick add tag
					</div>
					<div className="flex max-w-64 flex-wrap gap-1">
						{quickTagOptions.map((tag) => (
							<button
								key={tag.toLowerCase()}
								type="button"
								onClick={() => onAddTagToMRs?.(tag, targetMRs)}
								className="no-hover-lift"
								aria-label={`Add tag ${tag}`}
							>
								<TagPill tag={tag} className="max-w-[10rem]" />
							</button>
						))}
					</div>
				</div>
			)}
			<button
				type="button"
				role="menuitem"
				disabled={watchActionMRs.length === 0}
				onClick={() => {
					if (shouldUnwatch) onUnwatchMRs?.(watchActionMRs);
					else onWatchMRs?.(watchActionMRs);
				}}
				className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				{shouldUnwatch ? (
					<EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
				) : (
					<Eye className="h-4 w-4 text-[var(--color-text-secondary)]" />
				)}
				{watchLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				disabled={ignoreActionMRs.length === 0}
				onClick={() => {
					if (shouldUnignore) onUnignoreMRs?.(ignoreActionMRs);
					else onIgnoreMRs?.(ignoreActionMRs);
				}}
				className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				{shouldUnignore ? (
					<RotateCcw className="h-4 w-4 text-[var(--color-text-secondary)]" />
				) : (
					<EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
				)}
				{ignoreLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={onOpenRuleForm}
				className="no-hover-lift flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Ban className="h-4 w-4 text-[var(--color-text-secondary)]" />
				{excludeLabel}
			</button>
		</div>
	);
};

const ExcludeLikeDialog = ({ draft, onChange, onClose, onSubmit }) => {
	if (!draft) return null;

	const isBulk = !!draft.mrs;
	const canSubmit =
		(draft.includeTitle && (isBulk || draft.titleRegex?.trim())) ||
		(draft.includeRepo && (isBulk || draft.projectPath?.trim())) ||
		(draft.includeAuthor && (isBulk || draft.authorName?.trim()));

	const updateDraft = (patch) => onChange({ ...draft, ...patch });

	return (
		<dialog
			open
			className="fixed inset-0 z-50 m-0 flex h-screen max-h-none w-screen max-w-none items-center justify-center bg-black/60 px-4 text-left"
			aria-modal="true"
			onMouseDown={onClose}
		>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					if (canSubmit) onSubmit(draft);
				}}
				className="w-full max-w-md space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4 shadow-xl"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="flex items-center justify-between gap-3">
					<h2 className="text-base font-semibold text-[var(--color-text-primary)]">
						Exclude Like
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="no-hover-lift rounded-md p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
						aria-label="Close exclude dialog"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<label className="flex items-start gap-3 rounded-md border border-[var(--color-border)] p-3">
					<input
						type="checkbox"
						checked={draft.includeTitle}
						onChange={(event) =>
							updateDraft({ includeTitle: event.target.checked })
						}
						className="mt-1 h-4 w-4"
					/>
					<span className="min-w-0 flex-1">
						<span className="block text-sm font-medium">
							Title regex{isBulk ? " for each selected MR" : ""}
						</span>
						{!isBulk && (
							<input
								type="text"
								value={draft.titleRegex}
								onChange={(event) =>
									updateDraft({ titleRegex: event.target.value })
								}
								disabled={!draft.includeTitle}
								className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm disabled:opacity-60"
							/>
						)}
					</span>
				</label>
				<label className="flex items-center gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm font-medium">
					<input
						type="checkbox"
						checked={draft.includeRepo}
						onChange={(event) =>
							updateDraft({ includeRepo: event.target.checked })
						}
						className="h-4 w-4"
					/>
					{isBulk
						? "Repository for each selected MR"
						: `Repository: ${draft.projectPath || "Unknown"}`}
				</label>
				<label className="flex items-center gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm font-medium">
					<input
						type="checkbox"
						checked={draft.includeAuthor}
						onChange={(event) =>
							updateDraft({ includeAuthor: event.target.checked })
						}
						className="h-4 w-4"
					/>
					{isBulk
						? "Author for each selected MR"
						: `Author: ${draft.authorName || "Unknown"}`}
				</label>
				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="no-hover-lift rounded-md border border-[var(--color-border)] px-3 py-2 text-sm hover:bg-[var(--color-bg-secondary)]"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!canSubmit}
						className="no-hover-lift rounded-md bg-[var(--color-action)] px-3 py-2 text-sm font-medium text-[var(--color-action-text)] disabled:opacity-60"
					>
						Apply Rules
					</button>
				</div>
			</form>
		</dialog>
	);
};

const BulkSelectionToolbar = ({
	selectedCount,
	onClear,
	onCopyLinks,
	onIgnore,
	onOpenExcludeRules,
}) => {
	if (selectedCount === 0) return null;

	const buttonClass =
		"no-hover-lift inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]";

	return (
		<div className="flex h-8 min-w-0 flex-nowrap items-center justify-end gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1">
			<span className="whitespace-nowrap px-2 text-xs font-medium text-[var(--color-text-secondary)]">
				{selectedCount} selected
			</span>
			<div className="flex flex-nowrap items-center gap-1">
				<Tooltip content="Copy selected MR links">
					<button
						type="button"
						onClick={onCopyLinks}
						className={buttonClass}
						aria-label="Copy selected merge request links"
					>
						<Link2 className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Ignore selected MRs">
					<button
						type="button"
						onClick={onIgnore}
						className={buttonClass}
						aria-label="Ignore selected merge requests"
					>
						<EyeOff className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Exclude selected MRs">
					<button
						type="button"
						onClick={onOpenExcludeRules}
						className={buttonClass}
						aria-label="Exclude selected merge requests"
					>
						<Ban className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Clear selection">
					<button
						type="button"
						onClick={onClear}
						className={buttonClass}
						aria-label="Clear selection"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
			</div>
		</div>
	);
};

const RepoSection = ({
	controls,
	data,
	displayMRs,
	dragHandle,
	dragId,
	dragLabel,
	dropEdge = "before",
	enableCommentsExpansion,
	expanded,
	forceCollapsed = false,
	greyOutBlockingComments,
	isDragging = false,
	isDropTarget = false,
	onDragOver,
	onDrop,
	onLoadMore,
	onOpenDiff,
	onMRContextMenu,
	onRemove,
	onRefresh,
	onSectionDragEnd,
	onSectionDragStart,
	onToggle,
	onUpdateControls,
	page,
	repo,
	selectedMRKeySet,
	setPage,
	onClearSelection,
	onCopySelectedLinks,
	onIgnoreSelected,
	onOpenExcludeRules,
	onMRSelect,
	isWatched,
	getActivity,
	markSeen,
	searchQuery = "",
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const repoData = data || {};
	const visibleMRs = displayMRs.slice(
		(page - 1) * ITEMS_PER_PAGE,
		page * ITEMS_PER_PAGE,
	);
	const authorOptions = buildAuthorOptions(repoData.mrs || []);
	const projectUrl = safeHttpUrl(repoData.project?.webUrl || repo.webUrl);
	const project = {
		...repo,
		...(repoData.project || {}),
	};
	const projectFullPath = project?.fullPath || repo.fullPath;
	const sectionRef = useRef(null);
	const canDragSection = Boolean(dragId);
	const effectiveExpanded = expanded && !forceCollapsed;
	const [shouldRenderBody, setShouldRenderBody] = useState(effectiveExpanded);
	const sectionStateClassName = `${isDropTarget ? "is-drop-target" : ""} ${
		isDragging ? "is-dragging scale-[0.995] opacity-70" : ""
	}`;
	const sectionSelectedMRs = displayMRs.filter((mr) =>
		selectedMRKeySet.has(getMRKey(mr)),
	);
	const hasPagination = displayMRs.length > ITEMS_PER_PAGE;
	const canLoadMore = Boolean(repoData.pageInfo?.hasNextPage);

	useEffect(() => {
		if (effectiveExpanded) {
			setShouldRenderBody(true);
			return undefined;
		}

		const timer = setTimeout(() => {
			setShouldRenderBody(false);
		}, 100);
		return () => clearTimeout(timer);
	}, [effectiveExpanded]);

	const scrollSectionIntoView = () => {
		const section = sectionRef.current;
		if (!section || typeof window === "undefined") return;

		const prefersReducedMotion = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)",
		).matches;
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				section.scrollIntoView({
					block: "start",
					behavior: prefersReducedMotion ? "auto" : "smooth",
				});
			});
		});
	};

	const handleToggle = () => {
		const shouldExpand = !expanded;
		onToggle?.();
		if (shouldExpand) scrollSectionIntoView();
	};

	const handleSectionDragStart = (event) => {
		if (!dragId || !sectionRef.current) return;

		event.stopPropagation();
		const rect = sectionRef.current.getBoundingClientRect();
		const preview = sectionRef.current.cloneNode(true);
		preview.style.position = "fixed";
		preview.style.left = "-10000px";
		preview.style.top = "-10000px";
		preview.style.width = `${rect.width}px`;
		preview.style.maxHeight = "144px";
		preview.style.overflow = "hidden";
		preview.style.pointerEvents = "none";
		preview.style.opacity = "0.96";
		preview.style.transform = "scale(0.985)";
		preview.style.boxShadow = "var(--shadow-popover)";
		document.body.appendChild(preview);

		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData(SECTION_DRAG_TYPE, dragId);
		event.dataTransfer.setData("text/plain", `section:${dragId}`);
		event.dataTransfer.setDragImage(
			preview,
			Math.min(Math.max(event.clientX - rect.left, 24), rect.width - 24),
			Math.min(Math.max(event.clientY - rect.top, 24), 72),
		);

		window.setTimeout(() => preview.remove(), 0);
		onSectionDragStart?.(dragId);
	};

	const handleSectionDragEnd = (event) => {
		event.stopPropagation();
		onSectionDragEnd?.();
	};

	return (
		<section
			ref={sectionRef}
			className={`section-raised-row relative scroll-mt-4 transition-[background-color,border-color,box-shadow,opacity,transform] duration-150 ${sectionStateClassName}`}
			onDragOver={onDragOver}
			onDrop={onDrop}
		>
			{isDropTarget && (
				<div
					className={`pointer-events-none absolute left-0 right-0 z-20 h-0.5 rounded-full bg-[var(--color-accent)] ${
						dropEdge === "after" ? "-bottom-2" : "-top-2"
					}`}
				/>
			)}
			<div
				className={`section-raised-header relative flex flex-wrap items-center justify-between gap-3 px-3 py-2 transition-colors ${canDragSection ? "cursor-grab active:cursor-grabbing" : ""}`}
				draggable={canDragSection}
				onDragStart={handleSectionDragStart}
				onDragEnd={handleSectionDragEnd}
			>
				<button
					type="button"
					draggable={canDragSection}
					onClick={handleToggle}
					onDragStart={handleSectionDragStart}
					onDragEnd={handleSectionDragEnd}
					aria-expanded={effectiveExpanded}
					aria-label={
						effectiveExpanded ? "Collapse repository" : "Expand repository"
					}
					title={canDragSection ? `Drag to reorder ${dragLabel}` : undefined}
					className="no-hover-lift absolute inset-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
				/>
				<div className="pointer-events-none relative z-10 flex min-w-0 flex-1 items-center gap-1">
					<CountChip count={displayMRs.length} />
					<span>{dragHandle}</span>
					<span className="flex min-w-0 flex-1 items-center gap-2 text-left text-[var(--color-text-primary)]">
						{effectiveExpanded ? (
							<ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
						) : (
							<ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
						)}
						<FolderGit2 className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
						<span className="min-w-0">
							<span className="block truncate text-sm font-semibold">
								<HighlightedText query={searchQuery}>
									{repoName(project)}
								</HighlightedText>
							</span>
							{projectUrl ? (
								<a
									href={projectUrl}
									target="_blank"
									rel="noopener noreferrer"
									draggable="false"
									onClick={(event) => event.stopPropagation()}
									onDragStart={(event) => event.preventDefault()}
									className="pointer-events-auto block truncate text-xs font-normal text-[var(--color-text-secondary)] hover:text-[var(--color-link)] hover:underline"
								>
									<HighlightedText query={searchQuery}>
										{projectFullPath}
									</HighlightedText>
								</a>
							) : (
								<span className="block truncate text-xs font-normal text-[var(--color-text-secondary)]">
									<HighlightedText query={searchQuery}>
										{projectFullPath}
									</HighlightedText>
								</span>
							)}
						</span>
					</span>
				</div>

				<div className="relative z-10 flex items-center gap-1">
					<button
						type="button"
						onClick={onRefresh}
						onDragStart={(event) => event.preventDefault()}
						disabled={repoData.loading}
						className="no-hover-lift inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Refresh repository merge requests"
						title="Refresh"
					>
						<RefreshCcw
							className={`h-4 w-4 ${repoData.loading ? "animate-spin" : ""}`}
						/>
					</button>
					{projectUrl && (
						<a
							href={projectUrl}
							target="_blank"
							rel="noopener noreferrer"
							draggable="false"
							className="no-hover-lift inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							aria-label="Open repository in GitLab"
						>
							<ExternalLink className="h-4 w-4" />
						</a>
					)}
					<button
						type="button"
						onClick={onRemove}
						onDragStart={(event) => event.preventDefault()}
						className="no-hover-lift inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Remove repository"
					>
						<Trash2 className="h-4 w-4" />
					</button>
				</div>
			</div>

			{shouldRenderBody && (
				<div
					className={`grid transition-[grid-template-rows,opacity] duration-100 ease-out ${
						effectiveExpanded
							? "grid-rows-[1fr] opacity-100"
							: "grid-rows-[0fr] opacity-0"
					}`}
				>
					<div
						className={`min-h-0 overflow-hidden transition-transform duration-100 ease-out ${
							effectiveExpanded ? "translate-y-0" : "-translate-y-1"
						}`}
					>
						<div className="border-t border-[var(--color-section-raised-border)] px-3 py-3">
							<div className="mb-3 flex flex-wrap items-center justify-between gap-3">
								<div className="text-xs text-[var(--color-text-secondary)]">
									{repoData.totalCount != null
										? `${repoData.mrs?.length || 0} loaded of ${repoData.totalCount}`
										: `${repoData.mrs?.length || 0} loaded`}
								</div>
								{(repoData.mrs?.length || 0) > 0 && (
									<div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
										<SectionFilterControls
											authorOptions={authorOptions}
											value={controls}
											onChange={onUpdateControls}
										/>
										<BulkSelectionToolbar
											selectedCount={sectionSelectedMRs.length}
											onCopyLinks={() =>
												onCopySelectedLinks(sectionSelectedMRs)
											}
											onIgnore={() => onIgnoreSelected(sectionSelectedMRs)}
											onOpenExcludeRules={() =>
												onOpenExcludeRules(sectionSelectedMRs)
											}
											onClear={() => onClearSelection(sectionSelectedMRs)}
										/>
									</div>
								)}
							</div>

							{repoData.error && (
								<div className="mb-3 flex items-center gap-2 rounded-lg border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-sm text-[var(--color-error-text)]">
									<AlertCircle className="h-4 w-4 shrink-0" />
									<span>{repoData.error}</span>
								</div>
							)}

							{repoData.loading && (repoData.mrs?.length || 0) === 0 ? (
								<div className="flex h-48 items-center justify-center text-[var(--color-text-secondary)]">
									<Loader2 className="mr-2 h-5 w-5 animate-spin" />
									Loading merge requests...
								</div>
							) : displayMRs.length > 0 ? (
								<>
									{visibleMRs.map((mr) => (
										<MergeRequest
											key={getMRKey(mr)}
											mr={mr}
											greyOutBlockingComments={greyOutBlockingComments}
											isMyOpenMR={mr.author?.isMe}
											enableCommentsExpansion={enableCommentsExpansion}
											onSelect={(event, selectedMR) =>
												onMRSelect(event, selectedMR, visibleMRs)
											}
											onContextMenu={onMRContextMenu}
											isSelected={selectedMRKeySet.has(getMRKey(mr))}
											isWatched={isWatched?.(mr)}
											activity={getActivity?.(mr)}
											onActivitySeen={markSeen}
											onOpenDiff={onOpenDiff}
											searchQuery={searchQuery}
											tags={getMRTags(mr)}
											allTags={allMRTags}
											onAddTag={onAddMRTag}
											onRemoveTag={onRemoveMRTag}
										/>
									))}
									{(hasPagination || canLoadMore) && (
										<div className="mt-4 flex items-center justify-between gap-3">
											<div className="min-w-0 flex-1">
												<PaginationButtons
													total={displayMRs.length}
													currentPage={page}
													onPageChange={setPage}
													itemsPerPage={ITEMS_PER_PAGE}
													className="mt-0"
												/>
											</div>
											{canLoadMore && (
												<button
													type="button"
													onClick={onLoadMore}
													disabled={repoData.loading}
													className="no-hover-lift ml-auto inline-flex h-8 shrink-0 items-center gap-2 rounded-md border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
												>
													{repoData.loading ? (
														<Loader2 className="h-4 w-4 animate-spin" />
													) : (
														<Plus className="h-4 w-4" />
													)}
													Load more
												</button>
											)}
										</div>
									)}
								</>
							) : (
								<div className="rounded bg-[var(--color-info-bg)] px-4 py-3 text-[var(--color-info-text)]">
									No merge requests
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

export const ReposPage = ({
	enableCommentsExpansion = false,
	excludeProjects = [],
	excludeLikeRules = [],
	fullWidth = false,
	greyOutBlockingComments = true,
	ignoredMRIds = [],
	onAddExcludeLikeRule,
	onIgnoreMR,
	onUnignoreMR,
	onOpenDiff,
	quickFilters,
	searchQuery = "",
	showBotPRs = true,
	showDrafts = true,
	sortOption = "updatedAt",
	watchedMRState,
	activeTagFilter = [],
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const [repos, setRepos] = useLocalStorage("repos-page-repositories", []);
	const [expandedRepoPath, setExpandedRepoPath] = useLocalStorage(
		"repos-page-expanded-repo",
		null,
	);
	const [repoStates, setRepoStates] = useLocalStorage(
		"repos-page-state-filter",
		DEFAULT_REPO_STATES,
	);
	const [sectionControls, setSectionControls] = useLocalStorage(
		"repos-page-section-controls",
		{},
	);
	const [pageByRepo, setPageByRepo] = useState({});
	const [draggingRepoPath, setDraggingRepoPath] = useState(null);
	const [repoDropTarget, setRepoDropTarget] = useState(null);
	const [selectedMRKeys, setSelectedMRKeys] = useState([]);
	const [selectionAnchorKey, setSelectionAnchorKey] = useState(null);
	const [contextMenu, setContextMenu] = useState(null);
	const [excludeDraft, setExcludeDraft] = useState(null);
	const [tagDraftMRs, setTagDraftMRs] = useState(null);
	const contextMenuRef = useRef(null);
	const [repoPaths] = useLocalStorage("repoPaths", DEFAULT_REPO_PATHS);
	const { repoData, searchProjects, loadRepoMergeRequests, clearRepoData } =
		useRepositoryMergeRequests();
	const {
		watchedIds = [],
		watchMR,
		unwatchMR,
		isWatched,
		getActivity,
		markSeen,
	} = watchedMRState || {};
	const repoIds = repos.map((repo) => repo.fullPath);
	const { order: repoSectionOrder, moveSection: moveRepoSection } =
		useSectionOrder("repos-page-section-order", repoIds);
	const repoByPath = new Map(repos.map((repo) => [repo.fullPath, repo]));
	const orderedRepos = repoSectionOrder
		.map((fullPath) => repoByPath.get(fullPath))
		.filter(Boolean);

	const expandedRepo = repos.find((repo) => repo.fullPath === expandedRepoPath);
	const selectedMRKeySet = useMemo(
		() => new Set(selectedMRKeys),
		[selectedMRKeys],
	);
	const selectableMRByKey = useMemo(() => {
		const map = new Map();
		for (const data of Object.values(repoData)) {
			for (const mr of data?.mrs || []) {
				map.set(getMRKey(mr), mr);
			}
		}
		return map;
	}, [repoData]);
	useEffect(() => {
		if (
			expandedRepoPath &&
			!repos.some((repo) => repo.fullPath === expandedRepoPath)
		) {
			setExpandedRepoPath(null);
		}
	}, [expandedRepoPath, repos, setExpandedRepoPath]);

	useEffect(() => {
		if (!expandedRepo) return;
		const data = repoData[expandedRepo.fullPath];
		if (data?.loading) return;
		if (!data || !isRepositoryCacheFresh(data)) {
			loadRepoMergeRequests(expandedRepo);
		}
	}, [expandedRepo, loadRepoMergeRequests, repoData]);

	useEffect(() => {
		if (!contextMenu) return undefined;

		const handleMouseDown = (event) => {
			if (contextMenuRef.current?.contains(event.target)) return;
			setContextMenu(null);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setContextMenu(null);
		};
		const handleScroll = () => setContextMenu(null);

		document.addEventListener("mousedown", handleMouseDown);
		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("scroll", handleScroll, true);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("scroll", handleScroll, true);
		};
	}, [contextMenu]);

	const addRepo = (repo) => {
		setRepos((prev) => {
			if (prev.some((item) => item.fullPath === repo.fullPath)) return prev;
			return [...prev, repo];
		});
		setExpandedRepoPath(repo.fullPath);
		setPageByRepo((prev) => ({ ...prev, [repo.fullPath]: 1 }));
	};

	const removeRepo = (repo) => {
		setRepos((prev) => prev.filter((item) => item.fullPath !== repo.fullPath));
		clearRepoData(repo.fullPath);
		setSectionControls((prev) => {
			const next = { ...prev };
			delete next[repo.fullPath];
			return next;
		});
		setPageByRepo((prev) => {
			const next = { ...prev };
			delete next[repo.fullPath];
			return next;
		});
		if (expandedRepoPath === repo.fullPath) {
			setExpandedRepoPath(null);
		}
	};

	const toggleRepoState = (state) => {
		setRepoStates((prev) => {
			if (prev.includes(state)) {
				return prev.filter((item) => item !== state);
			}
			return [...prev, state];
		});
	};

	const updateControls = (fullPath, patch) => {
		setSectionControls((prev) => ({
			...prev,
			[fullPath]: {
				...DEFAULT_SECTION_CONTROLS,
				...prev[fullPath],
				...patch,
			},
		}));
	};

	const setRepoPage = (fullPath, page) => {
		setPageByRepo((prev) => ({ ...prev, [fullPath]: page }));
	};

	const refreshRepo = (repo) => {
		setRepoPage(repo.fullPath, 1);
		loadRepoMergeRequests(repo, { force: true });
	};

	const handleMRSelect = (event, mr, visibleMRs = []) => {
		const key = getMRKey(mr);
		if (!key) return;
		const isToggleModifier = event.metaKey || event.ctrlKey;
		if (!event.shiftKey && !isToggleModifier && onOpenDiff) {
			onOpenDiff(mr);
			return;
		}

		const visibleKeys = visibleMRs.map(getMRKey);
		const currentIndex = visibleKeys.indexOf(key);
		const anchorIndex = selectionAnchorKey
			? visibleKeys.indexOf(selectionAnchorKey)
			: -1;

		setSelectedMRKeys((prev) => {
			const next = new Set(prev);

			if (next.has(key)) {
				next.delete(key);
			} else if (
				event.shiftKey &&
				anchorIndex !== -1 &&
				currentIndex !== -1 &&
				selectionAnchorKey !== key
			) {
				const start = Math.min(anchorIndex, currentIndex);
				const end = Math.max(anchorIndex, currentIndex);
				for (const rangeKey of visibleKeys.slice(start, end + 1)) {
					next.add(rangeKey);
				}
			} else {
				next.add(key);
			}

			return [...next];
		});
		setSelectionAnchorKey(key);
	};

	const clearMRSelection = (mrs) => {
		const keysToClear = new Set(mrs.map(getMRKey));
		setSelectedMRKeys((prev) => prev.filter((key) => !keysToClear.has(key)));
		setSelectionAnchorKey((anchorKey) =>
			keysToClear.has(anchorKey) ? null : anchorKey,
		);
	};

	const clearAllMRSelection = () => {
		setSelectedMRKeys([]);
		setSelectionAnchorKey(null);
	};

	const handleMRContextMenu = (event, mr) => {
		event.preventDefault();
		event.stopPropagation();
		const mrKey = getMRKey(mr);
		const selectedTargets = selectedMRKeys
			.map((key) => selectableMRByKey.get(key))
			.filter(Boolean);
		const targetMRs =
			selectedTargets.length > 1 && selectedMRKeys.includes(mrKey)
				? selectedTargets
				: [mr];

		setExcludeDraft(null);
		setContextMenu({
			mr,
			targetMRs,
			...clampContextMenuPosition(event.clientX, event.clientY),
		});
	};

	const openExcludeRuleForm = (mrs = null) => {
		const targetMRs =
			mrs ||
			contextMenu?.targetMRs ||
			(contextMenu?.mr ? [contextMenu.mr] : []);
		if (targetMRs.length === 0) return;

		if (targetMRs.length > 1) {
			setExcludeDraft({
				mrs: targetMRs,
				includeTitle: false,
				includeRepo: true,
				includeAuthor: false,
			});
		} else {
			setExcludeDraft(buildExcludeDraft(targetMRs[0]));
		}
		setContextMenu(null);
	};

	const applyExcludeRules = (draft) => {
		const mrs = draft.mrs || [draft.mr];
		for (const mr of mrs) {
			const rule = draft.mrs
				? buildExcludeRuleFromMR(mr, draft)
				: {
						titleRegex: draft.includeTitle ? draft.titleRegex.trim() : "",
						projectPath: draft.includeRepo ? draft.projectPath : "",
						authorName: draft.includeAuthor ? draft.authorName : "",
						authorUsername: draft.includeAuthor ? draft.authorUsername : "",
						sourceMr: {
							id: mr.id,
							iid: mr.iid,
							title: mr.title,
							projectPath: getMRProjectPath(mr),
						},
					};
			if (rule.titleRegex || rule.projectPath || rule.authorName) {
				onAddExcludeLikeRule?.(rule, [mr]);
			}
		}
		setExcludeDraft(null);
		clearMRSelection(mrs);
	};

	const ignoreMRs = (mrs) => {
		for (const mr of mrs) {
			if (mr.id) onIgnoreMR?.(mr);
		}
		setContextMenu(null);
		clearMRSelection(mrs);
	};

	const unignoreMRs = (mrs) => {
		for (const mr of mrs) {
			if (mr.id) onUnignoreMR?.(mr);
		}
		setContextMenu(null);
		clearMRSelection(mrs);
	};

	const copyCheckoutFromMenu = (mr) => {
		copyCheckoutCommand(mr, repoPaths);
		setContextMenu(null);
	};

	const copyLinksAndClose = (mrs) => {
		copyMRLinks(mrs);
		setContextMenu(null);
	};

	const addTagToMRs = (tag, mrs) => {
		for (const mr of mrs) {
			onAddMRTag?.(mr, tag);
		}
		setContextMenu(null);
	};

	const openTagDialogFromMenu = (mrs) => {
		setTagDraftMRs(mrs);
		setContextMenu(null);
	};

	const watchMRs = (mrs) => {
		for (const mr of mrs) watchMR?.(mr);
		setContextMenu(null);
		clearAllMRSelection();
	};

	const unwatchMRs = (mrs) => {
		for (const mr of mrs) unwatchMR?.(mr);
		setContextMenu(null);
		clearAllMRSelection();
	};

	const clearRepoDrag = () => {
		setDraggingRepoPath(null);
		setRepoDropTarget(null);
	};

	const handleRepoDragOver = (event, targetPath) => {
		if (!draggingRepoPath || draggingRepoPath === targetPath) return;

		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		const rect = event.currentTarget.getBoundingClientRect();
		const edge =
			event.clientY < rect.top + rect.height / 2 ? "before" : "after";
		setRepoDropTarget((prev) =>
			prev?.id === targetPath && prev.edge === edge
				? prev
				: { id: targetPath, edge },
		);
	};

	const handleRepoDrop = (event, targetPath) => {
		if (!draggingRepoPath) return;

		event.preventDefault();
		const edge =
			repoDropTarget?.id === targetPath ? repoDropTarget.edge : "before";
		moveRepoSection(draggingRepoPath, targetPath, edge);
		clearRepoDrag();
	};

	const repoReorderProps = (repo) => ({
		dragHandle: (
			<SectionDragHandle
				id={repo.fullPath}
				label={repoLabel(repo)}
				interactive={false}
			/>
		),
		dragId: repo.fullPath,
		dragLabel: repoLabel(repo),
		dropEdge:
			repoDropTarget?.id === repo.fullPath ? repoDropTarget.edge : "before",
		isDragging: draggingRepoPath === repo.fullPath,
		isDropTarget:
			repoDropTarget?.id === repo.fullPath &&
			draggingRepoPath !== repo.fullPath,
		forceCollapsed: Boolean(draggingRepoPath),
		onSectionDragStart: setDraggingRepoPath,
		onSectionDragEnd: clearRepoDrag,
		onDragOver: (event) => handleRepoDragOver(event, repo.fullPath),
		onDrop: (event) => handleRepoDrop(event, repo.fullPath),
	});
	const containerClassName = fullWidth
		? "mx-auto max-w-none px-2"
		: "mx-auto max-w-[980px] px-2";

	return (
		<>
			<MRContextMenu
				menu={contextMenu}
				menuRef={contextMenuRef}
				ignoredMRIds={ignoredMRIds}
				watchedIds={watchedIds}
				onCopyCheckout={copyCheckoutFromMenu}
				onCopyMRLinks={copyLinksAndClose}
				onIgnoreMRs={ignoreMRs}
				onOpenRuleForm={() => openExcludeRuleForm()}
				onUnignoreMRs={unignoreMRs}
				onUnwatchMRs={unwatchMRs}
				onWatchMRs={watchMRs}
				allMRTags={allMRTags}
				getMRTags={getMRTags}
				onAddTagToMRs={addTagToMRs}
				onOpenTagDialog={openTagDialogFromMenu}
			/>
			<ExcludeLikeDialog
				draft={excludeDraft}
				onChange={setExcludeDraft}
				onClose={() => setExcludeDraft(null)}
				onSubmit={applyExcludeRules}
			/>
			<MRTagDialog
				open={!!tagDraftMRs}
				mrs={tagDraftMRs || []}
				allTags={allMRTags}
				onClose={() => setTagDraftMRs(null)}
				onSubmit={addTagToMRs}
			/>
			<div className={containerClassName}>
				<div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-3 shadow-sm">
					<div className="flex flex-wrap items-center gap-3">
						<RepoSearchDropdown
							existingRepos={repos}
							onAdd={addRepo}
							searchProjects={searchProjects}
						/>
						<StateFilterBar states={repoStates} onToggle={toggleRepoState} />
					</div>
				</div>

				{repos.length === 0 ? (
					<div className="rounded bg-[var(--color-info-bg)] px-4 py-3 text-[var(--color-info-text)]">
						No repositories added
					</div>
				) : (
					<div className="space-y-3 pb-8">
						{orderedRepos.map((repo) => {
							const data = repoData[repo.fullPath];
							const controls = {
								...DEFAULT_SECTION_CONTROLS,
								...sectionControls[repo.fullPath],
							};
							const displayMRs = filterAndSortMRs({
								mrs: data?.mrs || [],
								controls,
								excludeProjects,
								excludeLikeRules,
								ignoredMRIds,
								quickFilters,
								repoStates,
								searchQuery,
								showBotPRs,
								showDrafts,
								sortOption,
								activeTagFilter,
								getMRTags,
							});
							const pageCount = Math.max(
								1,
								Math.ceil(displayMRs.length / ITEMS_PER_PAGE),
							);
							const page = Math.min(pageByRepo[repo.fullPath] || 1, pageCount);

							return (
								<RepoSection
									key={repo.fullPath}
									repo={repo}
									{...repoReorderProps(repo)}
									data={data}
									displayMRs={displayMRs}
									controls={controls}
									page={page}
									setPage={(nextPage) => setRepoPage(repo.fullPath, nextPage)}
									expanded={expandedRepoPath === repo.fullPath}
									onToggle={() =>
										setExpandedRepoPath(
											expandedRepoPath === repo.fullPath ? null : repo.fullPath,
										)
									}
									onRemove={() => removeRepo(repo)}
									onRefresh={() => refreshRepo(repo)}
									onUpdateControls={(patch) =>
										updateControls(repo.fullPath, patch)
									}
									onMRSelect={handleMRSelect}
									onMRContextMenu={handleMRContextMenu}
									onOpenDiff={onOpenDiff}
									selectedMRKeySet={selectedMRKeySet}
									onClearSelection={clearMRSelection}
									onCopySelectedLinks={copyMRLinks}
									onIgnoreSelected={ignoreMRs}
									onOpenExcludeRules={openExcludeRuleForm}
									isWatched={isWatched}
									getActivity={getActivity}
									markSeen={markSeen}
									onLoadMore={() => loadRepoMergeRequests(repo)}
									greyOutBlockingComments={greyOutBlockingComments}
									enableCommentsExpansion={enableCommentsExpansion}
									searchQuery={searchQuery}
									getMRTags={getMRTags}
									allMRTags={allMRTags}
									onAddMRTag={onAddMRTag}
									onRemoveMRTag={onRemoveMRTag}
								/>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
};
